import React, { useCallback, useEffect, useMemo, useState } from 'react';
import RoundTabs from './components/RoundTabs';
import BracketCanvas from './components/BracketCanvas';
import { useSports } from 'containers/Mundial2026/context/SportsProvider';
import { transformBracketTournament } from 'containers/Mundial2026/utils/standingsTransformer';
import styles from './TournamentView.module.scss';
import { useMundialMetrics } from 'containers/Mundial2026/hooks/useMundialMetrics';
import { INTERACTION_TYPES } from 'containers/Mundial2026/hooks/useMundialMetrics/constants';
import { useMundialScrollTracker } from 'containers/Mundial2026/hooks/useMundialScrollTracker';

const CANVAS_HEIGHT_BY_ROUND = {
  round_of_32: 1584, // Dieciseisavos
  round_of_16: 852, // Octavos
};
// Misma orden de magnitud que octavos: el overlay (.wc-standings__view) debe poder
// hacer scroll para que useStandingsScroll colapse las tabs (Fase de grupos | …).
// Con ~494px el bloque cabía en pantalla y no había scroll en cuartos/semis/final.
const DEFAULT_CANVAS_HEIGHT = 852;

const getCanvasHeight = roundId =>
  CANVAS_HEIGHT_BY_ROUND[roundId] != null
    ? CANVAS_HEIGHT_BY_ROUND[roundId]
    : DEFAULT_CANVAS_HEIGHT;

const getRoundIdFromStage = stage => (stage ? stage.toLowerCase() : null);

const TournamentView = () => {
  const { standings, loadView } = useSports();

  useEffect(
    () => {
      loadView('bracket');
    },
    [loadView],
  );

  const tournament = useMemo(() => transformBracketTournament(standings), [
    standings,
  ]);

  const rounds = tournament.rounds;
  const [activeRoundId, setActiveRoundId] = useState('round_of_32');
  const { trackSelectContent } = useMundialMetrics();

  useEffect(() => {
    const currentRoundId = getRoundIdFromStage(standings?.competition?.currentStage);
    if (!currentRoundId || !rounds?.some(round => round.id === currentRoundId)) {
      return;
    }
    setActiveRoundId(currentRoundId);
  }, [rounds, standings?.competition?.currentStage]);

  const handleRoundChange = useCallback(roundId => {
    setActiveRoundId(prev => (prev === roundId ? prev : roundId));
    const round = tournament.rounds?.find(r => r.id === roundId);
    const roundLabel = round?.label || roundId;
    trackSelectContent({
      pageTitleSegments: ['tabla de posiciones', 'vista torneo', roundLabel],
      pageTitle: roundLabel,
      contentSubsection: 'tabla de posiciones',
      moduloName: roundLabel,
      interactionType: INTERACTION_TYPES.GROUP_TABS,
    });
  }, [tournament.rounds, trackSelectContent]);

  useMundialScrollTracker({
    pageTitleSegments: ['tabla de posiciones', 'vista torneo', activeRoundId],
    contentSubsection: 'tabla de posiciones',
    moduloName: activeRoundId,
    scrollSelectors: ['[data-role="wc-standings-view"]']
  });

  const isLoading =
    !standings ||
    standings?.isFetched?.bracket !== true ||
    standings?.isLoading?.bracket;

  if (isLoading)
    return (
      <div className={styles['tournament-view']} style={{ minHeight: 740 }} />
    );

  if (!rounds || rounds.length === 0)
    return (
      <div className={styles['tournament-view']} style={{ minHeight: 740 }} />
    );

  const tabRounds = rounds.map(r => ({ id: r.id, label: r.label }));

  return (
    <div className={styles['tournament-view']}>
      <RoundTabs
        rounds={tabRounds}
        activeRoundId={activeRoundId}
        onRoundChange={handleRoundChange}
      />
      <div
        className={styles['tournament-view__canvas']}
        style={{ height: getCanvasHeight(activeRoundId) }}
      >
        <BracketCanvas rounds={rounds} activeRoundId={activeRoundId} />
      </div>
    </div>
  );
};

export default TournamentView;
